/**
 * Ручная форма приёма «Добавить обращение» (ТЗ §7): `POST /api/inbox/manual`, multipart.
 *
 * Дата поступления обязательна — именно от неё мини считает сроки по 59-ФЗ,
 * поэтому она вынесена наверх и снабжена пояснением.
 *
 * Вложения: PDF, сканы и фото, DOCX и текст — текст читается на сервере; старый DOC не принимается.
 * Проверка стоит прямо на выборе файла: сервер тоже ответит 415, но пользователь не должен
 * узнавать об этом после долгой загрузки. Отклоняем и по mime, и по расширению —
 * этим занимается общий `checkUploadFile`, здесь добавлена лишь проверка на форматы
 * за пределами `accept`.
 */

import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ACCEPTED_UPLOAD_MIME, checkUploadFile } from '@/api/client';
import { useCreateManualInbox } from '@/api/hooks';
import type { Source } from '@/api/types';
import { errorMessage, FormError } from '@/components/ErrorState';
import { SOURCE_LABELS } from '@/lib/stages';
import IngestProgress from '@/components/inbox/IngestProgress';

/** Источники, доступные при ручном приёме: `telegram_mini` и `demo` заводятся не отсюда. */
const MANUAL_SOURCES: readonly Source[] = ['manual', 'gosuslugi', 'email', 'site', 'bot'];

/** Расширения, которые считаем изображением или PDF, когда браузер не дал mime. */
const ALLOWED_EXT = /\.(png|jpe?g|gif|webp|bmp|tiff?|heic|heif|pdf)$/i;

function validateFile(file: File): string | null {
    const denied = checkUploadFile(file);
    if (denied) return denied;
    if (file.type.startsWith('image/') || file.type === 'application/pdf') return null;
    if (ALLOWED_EXT.test(file.name.toLowerCase())) return null;
    return 'Принимаются только изображения и PDF. Приложите другой файл или вставьте текст обращения.';
}

/** Значение для `<input type="datetime-local">`: локальное время без зоны. */
function localNowInput(): string {
    const now = new Date();
    const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

export default function NewAppealPage() {
    const navigate = useNavigate();
    const fileRef = useRef<HTMLInputElement>(null);

    const [source, setSource] = useState<Source>('manual');
    const [receivedAt, setReceivedAt] = useState(localNowInput);
    const [applicantName, setApplicantName] = useState('');
    const [applicantContact, setApplicantContact] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [inboxId, setInboxId] = useState<string | null>(null);

    const submit = useCreateManualInbox();

    const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const picked = event.currentTarget.files?.[0] ?? null;
        if (!picked) {
            setFile(null);
            setFileError(null);
            return;
        }
        const problem = validateFile(picked);
        if (problem) {
            setFile(null);
            setFileError(problem);
            if (fileRef.current) fileRef.current.value = '';
            return;
        }
        setFile(picked);
        setFileError(null);
    };

    const resetForm = () => {
        setInboxId(null);
        setSubject('');
        setBody('');
        setApplicantName('');
        setApplicantContact('');
        setReceivedAt(localNowInput());
        setFile(null);
        setFileError(null);
        setFormError(null);
        if (fileRef.current) fileRef.current.value = '';
        submit.reset();
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);

        if (!receivedAt) {
            setFormError('Укажите дату поступления: именно от неё считаются сроки по 59-ФЗ.');
            return;
        }
        const received = new Date(receivedAt);
        if (Number.isNaN(received.getTime())) {
            setFormError('Дата поступления введена неверно.');
            return;
        }
        if (received.getTime() > Date.now() + 60_000) {
            setFormError('Дата поступления не может быть в будущем.');
            return;
        }
        if (!body.trim() && !file) {
            setFormError('Введите текст обращения или приложите файл.');
            return;
        }
        if (fileError) return;

        const form = new FormData();
        form.set('source', source);
        form.set('received_at', received.toISOString());
        if (applicantName.trim()) form.set('applicant_name', applicantName.trim());
        if (applicantContact.trim()) form.set('applicant_contact', applicantContact.trim());
        if (subject.trim()) form.set('subject', subject.trim());
        if (body.trim()) form.set('body', body.trim());
        if (file) form.set('file', file);

        submit.mutate(form, {
            onSuccess: (accepted) => setInboxId(accepted.inbox_id),
        });
    };

    return (
        <>
            <div className="page-header">
                <h1>
                    ➕ Добавить обращение
                    <span>ручной приём</span>
                </h1>
                <Link className="update-info" to="/inbox">
                    ← к списку входящих
                </Link>
            </div>

            {inboxId ? (
                <>
                    <IngestProgress inboxId={inboxId} autoOpenCase onClose={() => navigate('/inbox')} />
                    <div className="draft-bar">
                        <button type="button" className="act-btn" onClick={resetForm}>
                            Добавить ещё одно обращение
                        </button>
                        <button type="button" className="act-btn" onClick={() => navigate('/inbox')}>
                            К списку входящих
                        </button>
                    </div>
                </>
            ) : (
                <div className="stat-block">
                    <div className="block-header">
                        <span>📝 Обращение гражданина</span>
                        <span className="checklist-progress">поля со звёздочкой обязательны</span>
                    </div>
                    <div className="block-body">
                        <form onSubmit={onSubmit}>
                            <div className="case-section">
                                <h4>Источник</h4>
                                <select
                                    className="act-select"
                                    value={source}
                                    aria-label="Источник обращения"
                                    onChange={(event) => setSource(event.target.value as Source)}
                                >
                                    {MANUAL_SOURCES.map((value) => (
                                        <option key={value} value={value}>
                                            {SOURCE_LABELS[value]}
                                        </option>
                                    ))}
                                </select>
                                <div className="draft-note">
                                    Откуда обращение пришло на самом деле. По умолчанию — внесено вручную.
                                </div>
                            </div>

                            <div className="case-section">
                                <h4>Дата поступления *</h4>
                                <input
                                    type="datetime-local"
                                    className="search-input"
                                    value={receivedAt}
                                    max={localNowInput()}
                                    onChange={(event) => setReceivedAt(event.target.value)}
                                    aria-label="Дата и время поступления обращения"
                                    required
                                />
                                <div className="draft-note">
                                    От этой даты, а не от даты внесения в панель, считаются сроки по 59-ФЗ:
                                    три дня на регистрацию и 30 дней на ответ (14 — по запросу медицинских
                                    документов). Ставьте фактическую дату поступления обращения в организацию.
                                </div>
                            </div>

                            <div className="case-section">
                                <h4>Заявитель</h4>
                                <input
                                    type="text"
                                    className="search-input"
                                    size={40}
                                    value={applicantName}
                                    onChange={(event) => setApplicantName(event.target.value)}
                                    placeholder="Фамилия Имя Отчество"
                                    aria-label="ФИО заявителя"
                                />
                            </div>

                            <div className="case-section">
                                <h4>Контакт для ответа</h4>
                                <input
                                    type="text"
                                    className="search-input"
                                    size={40}
                                    value={applicantContact}
                                    onChange={(event) => setApplicantContact(event.target.value)}
                                    placeholder="Телефон, электронная почта или почтовый адрес"
                                    aria-label="Контакт заявителя"
                                />
                            </div>

                            <div className="case-section">
                                <h4>Тема</h4>
                                <input
                                    type="text"
                                    className="search-input"
                                    size={60}
                                    value={subject}
                                    onChange={(event) => setSubject(event.target.value)}
                                    placeholder="Коротко, о чём обращение"
                                    aria-label="Тема обращения"
                                />
                            </div>

                            <div className="case-section">
                                <h4>Текст обращения</h4>
                                <textarea
                                    className="draft-area"
                                    value={body}
                                    onChange={(event) => setBody(event.target.value)}
                                    placeholder="Вставьте или наберите текст обращения так, как его подал заявитель."
                                    aria-label="Текст обращения"
                                />
                                <div className="draft-note">
                                    Нужен либо текст, либо файл. Если приложен скан, текст можно не заполнять —
                                    ИИ распознает вложение сам.
                                </div>
                            </div>

                            <div className="case-section">
                                <h4>Вложение</h4>
                                <input
                                    type="file"
                                    ref={fileRef}
                                    accept={ACCEPTED_UPLOAD_MIME}
                                    onChange={onFileChange}
                                    aria-label="Файл обращения"
                                />
                                <div className="draft-note">
                                    Принимаются PDF, сканы и фото (в том числе HEIC), DOCX и текстовые файлы: текст
                                    распознаётся автоматически. Старый формат DOC — сохраните как DOCX или PDF.
                                </div>
                                <FormError>{fileError}</FormError>
                            </div>

                            <FormError>{formError}</FormError>
                            <FormError>{submit.isError ? errorMessage(submit.error) : null}</FormError>

                            <div className="draft-bar">
                                <button type="submit" className="act-btn primary" disabled={submit.isPending}>
                                    {submit.isPending ? 'Отправляем…' : 'Отправить в разбор'}
                                </button>
                                <button
                                    type="button"
                                    className="act-btn"
                                    disabled={submit.isPending}
                                    onClick={() => navigate('/inbox')}
                                >
                                    Отмена
                                </button>
                                <span className="draft-note">
                                    После отправки обращение уходит в ИИ-разбор: он занимает обычно 5–10 минут,
                                    длинное обращение — до 15.
                                </span>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}
